import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { cartApi } from '@/api'

/**
 * Cart Store with API Integration
 * 
 * Supports both local storage (for guests) and server sync (for authenticated users)
 */

const GUEST_CART_KEY = 'inversun-guest-cart'

export const useCartStore = defineStore('cart', () => {
  // State
  const items = ref([])
  const isOpen = ref(false)
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const error = ref(null)
  const cartData = ref(null) // Server cart data (totals, etc.)

  // Load cart from localStorage (only in browser)
  const loadLocalCart = () => {
    if (typeof window === 'undefined' || isInitialized.value) return
    
    try {
      const savedCart = localStorage.getItem(GUEST_CART_KEY)
      if (savedCart) {
        items.value = JSON.parse(savedCart)
      }
      isInitialized.value = true
    } catch (e) {
      console.warn('Failed to load cart from localStorage:', e)
      items.value = []
    }
  }

  // Save to localStorage on change (only in browser)
  watch(items, (newItems) => {
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(newItems))
    } catch (e) {
      console.warn('Failed to save cart to localStorage:', e)
    }
  }, { deep: true })

  // Getters
  const itemCount = computed(() =>
    items.value.reduce((sum, item) => sum + item.quantity, 0)
  )
  const totalItems = computed(() => itemCount.value)

  const subtotal = computed(() => {
    if (cartData.value?.subtotal) {
      return parseFloat(cartData.value.subtotal)
    }
    return items.value.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
  })

  const total = computed(() => {
    if (cartData.value?.total) {
      return parseFloat(cartData.value.total)
    }
    return subtotal.value
  })

  const discount = computed(() => {
    if (cartData.value?.discount_amount) {
      return parseFloat(cartData.value.discount_amount)
    }
    return 0
  })

  const isEmpty = computed(() => !items.value || items.value.length === 0)

  // Actions
  async function fetchCart() {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await cartApi.getCart()
      // Handle nested response structure: { data: { items, subtotal, ... } }
      const cartPayload = response?.data || response

      cartData.value = cartPayload
      if (cartPayload?.items) {
        items.value = cartPayload.items
      }
      return cartPayload
    } catch (err) {
      error.value = err.message || 'Failed to fetch cart'
      console.error('Error fetching cart:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function addItem(product, variant = null, quantity = 1) {
    const existing = items.value.find(item => 
      item.product_id === product.id && item.variant_id === (variant?.id || null)
    )
    
    const unitPrice = variant ? Number(variant.price) : Number(product.price) || 0
    const itemName = variant ? `${product.name} - ${variant.name}` : product.name
    const itemImage = variant?.image || product.image
    const itemSku = variant?.sku || product.sku

    // Update local state immediately for responsiveness
    if (existing) {
      existing.quantity += quantity
    } else {
      items.value.push({
        id: product.id,
        product_id: product.id,
        variant_id: variant?.id || null,
        name: itemName,
        slug: product.slug,
        sku: itemSku,
        unit_price: unitPrice,
        total_price: unitPrice * quantity,
        image: itemImage,
        quantity,
      })
    }

    // Sync with server
    try {
      await cartApi.addItem({
        product_id: product.id,
        variant_id: variant?.id || null,
        quantity,
      })
      await fetchCart() // Refresh to get server totals
    } catch (err) {
      console.warn('Failed to sync cart with server:', err)
    }
  }

  async function updateQuantity(itemId, quantity) {
    const item = items.value.find(item => item.id === itemId)
    if (!item) return

    if (quantity <= 0) {
      await removeItem(itemId)
      return
    }

    item.quantity = quantity
    item.total_price = item.unit_price * quantity

    // Sync with server
    try {
      await cartApi.updateItem(itemId, quantity)
      await fetchCart()
    } catch (err) {
      console.warn('Failed to update cart item on server:', err)
    }
  }

  async function removeItem(itemId) {
    const index = items.value.findIndex(item => item.id === itemId)
    if (index > -1) {
      items.value.splice(index, 1)

      // Sync with server
      try {
        await cartApi.removeItem(itemId)
        await fetchCart()
      } catch (err) {
        console.warn('Failed to remove cart item from server:', err)
      }
    }
  }

  async function clearCart() {
    items.value = []

    try {
      const response = await cartApi.clearCart()
      // Handle nested response structure: { data: { items, subtotal, ... } }
      const cartPayload = response?.data || response
      
      cartData.value = cartPayload
      if (cartPayload?.items) {
        items.value = cartPayload.items
      }
    } catch (err) {
      console.warn('Failed to clear cart on server:', err)
    }
  }

  function toggleCart() {
    isOpen.value = !isOpen.value
  }

  function openCart() {
    isOpen.value = true
  }

  function closeCart() {
    isOpen.value = false
  }

  return {
    // State
    items,
    isOpen,
    isLoading,
    error,
    cartData,
    // Getters
    itemCount,
    totalItems,
    subtotal,
    total,
    discount,
    isEmpty,
    // Actions
    fetchCart,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    loadLocalCart
  }
})
